import * as React from 'react'

import { CalendarIcon, Clock2Icon, PlusIcon, TreePineIcon } from 'lucide-react'
import { inject, observer } from 'mobx-react'

import { IProjectClosedInterval } from '~/client/graph/types.generated'
import DesktopEventStore from '~/client/src/desktop/stores/EventStore/DesktopEvents.store'
import { Button } from '~/client/src/shared/components/Button/Button'
import { FieldTrigger } from '~/client/src/shared/components/FieldTrigger'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/client/src/shared/components/shadcn-ui/select'
import DateTimeFormat from '~/client/src/shared/enums/DateTimeFormat'
import KnownTranslatorKeys from '~/client/src/shared/localization/knownTranslatorKeys'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import Project from '~/client/src/shared/models/Project'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'
import {
  getTimezoneById,
  getTimezoneIdByCoordinates,
  getTimezoneName,
  timezones,
} from '~/client/src/shared/utils/timezones'

import ProjectSettingsStore from '../../ProjectSettings.store'
import ClosuresDatePickerStore from '../ClosuresDatePicker.store'
import { SettingsField, SettingsFormSection } from '../SettingsFormSection'
import ProjectScheduleSection from './ProjectScheduleSection'

interface IProps {
  store: ProjectSettingsStore
  datePickerStore: ClosuresDatePickerStore
}

interface InjectedProps extends IProps {
  eventsStore?: DesktopEventStore
  projectDateStore?: ProjectDateStore
}

const ICON_CLASS = 'tw:size-4 tw:shrink-0'
const SELECT_TRIGGER_CLASS = 'tw:w-full tw:min-w-0'

const ProjectTimeSettings: React.FC<InjectedProps> = observer(
  ({ store, datePickerStore, eventsStore, projectDateStore }) => {
    const { activeProject, timezoneId, dateTimeFormat } = store
    const { latitude, longitude } = eventsStore.appState

    const currentTimezoneId =
      timezoneId || getTimezoneIdByCoordinates(longitude, latitude)
    const selectedTimezoneId = getTimezoneById(currentTimezoneId).tzCode

    const timezoneItems = timezones.map(timezone => ({
      value: timezone.tzCode,
      label: getTimezoneName(timezone),
    }))

    // Stable across renders: it seeds the closure picker and keys
    // ProjectDateStore's format cache, which a fresh Date would miss every time.
    const now = React.useMemo(() => new Date(), [])
    const formatSample = (format: string) =>
      projectDateStore.format(now, format, Localization.dateLocale)
    const dateFormatItems = [
      {
        value: DateTimeFormat.US,
        label: `${Localization.translator.us} (${formatSample(
          projectDateStore.usFormat.weekdayMonthDayYearAndTime,
        )})`,
      },
      {
        value: DateTimeFormat.International,
        label: `${Localization.translator.international} (${formatSample(
          projectDateStore.internationalFormat.weekdayMonthDayYearAndTime,
        )})`,
      },
    ]

    const onTimezoneChange = (value: string) => {
      store.preConfimedTimezoneId = value
      store.toggleConfirmationModal()
    }

    const removeClosingInterval = (
      item: Project,
      interval: IProjectClosedInterval,
    ) => {
      if (!item.closedIntervals) {
        return
      }
      store.removeClosure(interval)
    }

    /** Writes the picker's dates back, replacing `index` or appending a new one. */
    const applyClosure = (index?: number) => {
      const { startDate, endDate, intevalName } = datePickerStore
      if (!startDate) {
        return
      }

      store.upsertClosure(
        {
          name: intevalName || Localization.translator.closureName,
          startDate: startDate.getTime(),
          endDate: (endDate || projectDateStore.endOfDay(startDate)).getTime(),
        },
        index,
      )
    }

    const openClosurePicker = (index?: number) => {
      const interval = index == null ? null : store.closures[index]

      datePickerStore.showWithOptions({
        titleTranslatorKey: KnownTranslatorKeys.addCustomProjectClosures,
        initialRange: {
          startDate: interval
            ? new Date(interval.startDate)
            : projectDateStore.startOfDay(now),
          endDate: interval?.endDate
            ? new Date(interval.endDate)
            : projectDateStore.endOfDay(now),
        },
        intervalName: interval?.name,
        handler: () => applyClosure(index),
        isSeparatedMonths: true,
        isTimeSensitive: true,
        activeProject,
        onRemove: removeClosingInterval,
      })
    }

    return (
      <div className="tw:flex tw:w-full tw:justify-center tw:py-6">
        <div className="tw:flex tw:w-[608px] tw:max-w-full tw:flex-col tw:gap-6">
          <SettingsFormSection title={Localization.translator.dateAndTime}>
            <SettingsField
              label={
                <>
                  <Clock2Icon className={ICON_CLASS} aria-hidden="true" />
                  {Localization.translator.timeZone}
                </>
              }
            >
              <Select
                value={selectedTimezoneId}
                onValueChange={value => onTimezoneChange(value as string)}
                items={timezoneItems}
              >
                <SelectTrigger
                  className={SELECT_TRIGGER_CLASS}
                  aria-label={Localization.translator.timeZone}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {timezoneItems.map(item => (
                    <SelectItem key={item.value} value={item.value}>
                      {item.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </SettingsField>

            <SettingsField
              label={
                <>
                  <CalendarIcon className={ICON_CLASS} aria-hidden="true" />
                  {Localization.translator.dateFormat}
                </>
              }
            >
              <Select
                value={dateTimeFormat}
                onValueChange={value =>
                  store.setDateTimeFormat(value as DateTimeFormat)
                }
                items={dateFormatItems}
              >
                <SelectTrigger
                  className={SELECT_TRIGGER_CLASS}
                  aria-label={Localization.translator.dateFormat}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {dateFormatItems.map(item => (
                    <SelectItem key={item.value} value={item.value}>
                      {item.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </SettingsField>
          </SettingsFormSection>

          <ProjectScheduleSection
            store={store}
            projectDateStore={projectDateStore}
          />

          <SettingsFormSection title={Localization.translator.holidays}>
            {store.closures.map((interval, index) => (
              <SettingsField
                key={index}
                label={
                  <>
                    <TreePineIcon className={ICON_CLASS} aria-hidden="true" />
                    <span className="tw:truncate">{interval.name}</span>
                  </>
                }
              >
                <FieldTrigger
                  aria-label={interval.name}
                  onClick={() => openClosurePicker(index)}
                >
                  {projectDateStore.getDateYearIntervalToDisplay(
                    interval.startDate,
                    interval.endDate,
                  )}
                </FieldTrigger>
              </SettingsField>
            ))}
            <Button variant="primary-ghost" onClick={() => openClosurePicker()}>
              <PlusIcon data-icon="inline-start" />
              {Localization.translator.addNew}
            </Button>
          </SettingsFormSection>
        </div>
      </div>
    )
  },
)

export default inject('eventsStore', 'projectDateStore')(ProjectTimeSettings)
